import React, { useState, useMemo } from 'react';
import { Table, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import CreatableSelect from 'react-select/creatable';
import Nodatacomponent from 'components/Nodatacomponent';
import { getFormatCurrency, getRandomID, truncateString } from 'functions';
import { Footerdata, SelectOption, TableHead } from 'types';

const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

type Props = {
  Theads: TableHead[];
  tablebutton: (product: object, index?: number) => JSX.Element;
  onRowClick: (product: object, index?: number) => void;
  data: object[];
  /** optional extra class for a single row, eg. to flag it */
  rowClassName?: (product: object, index?: number) => string;
  isHover: boolean;
  className: string;
  click: (data: any, e: any, index?: any) => void;
  footer: boolean;
  footerdata?: Footerdata;
  Tags?: {
    value: any;
    options: any;
    styles: any;
    onCreateOption: any;
    onChange: any;
  };
};

const TableC: React.FC<Props> = ({
  Theads,
  tablebutton,
  data,
  className,
  click,
  footer = false,
  footerdata,
  Tags,
  isHover = false,
  onRowClick = () => {},
  rowClassName = () => '',
}) => {
  const currencyCode = localStorage.getItem('currencyCode');
  const [open, setOpen] = useState<boolean>(false);
  const [itemsPerPage, setItemsPerPage] = useState<number>(footerdata?.pageLimit || 0);

  const formatDate = (epochTime) => {
    const date = new Date(epochTime * 1000);
    return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${date.getFullYear()}`;
  };

  const gotoPage = (page, itemLimit = itemsPerPage) => {
    footerdata?.pagechange({
      currentPage: page,
      totalPages: footerdata.totalpages,
      pageLimit: itemLimit,
      searchInput: footerdata.searchInput,
    });
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    gotoPage(footerdata?.currentpage, value);
  };

  const handleMoveLeft = (evt) => {
    evt.preventDefault();
    if (footerdata?.currentpage > 1) {
      gotoPage(footerdata.currentpage - 1);
    }
  };

  const handleMoveRight = (evt) => {
    evt.preventDefault();
    if (footerdata?.currentpage < footerdata?.totalpages) {
      gotoPage(footerdata.currentpage + 1);
    }
  };

  const memoizedTableRows = useMemo(() => {
    if (data.length === 0) {
      return (
        <tr className="text-center">
          <td colSpan={Theads.length}>
            <div className="d-flex justify-content-center" style={{ height: '10rem' }}>
              <div className="w-50">
                <Nodatacomponent />
              </div>
            </div>
          </td>
        </tr>
      );
    }

    return data.map((item, index) => (
      <tr
        key={index}
        className={`${isHover ? 'cursor-pointer' : ''} ${rowClassName(item, index)}`.trim()}
        onClick={() => onRowClick(item, index)}
      >
        {Theads.map((col, colIndex) => {
          if (col.key === 'index') return <td key={colIndex}>{index + 1}</td>;
          if (col.key === 'S.No.') return <td key={colIndex}>{col.value + index + 1}</td>;
          if (col.key === 'button')
            return (
              <td key={colIndex} onClick={(e) => e.stopPropagation()}>
                {tablebutton(item, index)}
              </td>
            );
          if (col.key[0] === 'multi')
            return (
              <td key={colIndex} className="Innertable">
                {col.key.map((i, idx) => (
                  <React.Fragment key={idx}>
                    <span>{item[i]}</span>
                    <br />
                  </React.Fragment>
                ))}
              </td>
            );
          if (col.key === 'Image')
            return (
              <td key={colIndex}>
                <img
                  src={item[col.value] || '/manage/images/product_placeholder.png'}
                  alt="product"
                  width="100px"
                  height="100px"
                  onClick={() => col.click?.(item[col.value])}
                  onError={(e) => (e.currentTarget.src = '/manage/images/product_placeholder.png')}
                />
              </td>
            );
          //  if (col.key === 'answerJson') {
          //     var data = item[col.key];

          //     return <td key={col.id}>{data[col?.value] || 'N/A'}</td>;
          //   }
          if (col.key === 'status')
            return (
              <td key={colIndex} style={{ color: item[col.value] ? 'green' : 'red' }}>
                <div className="display-statuss">
                  <span>{item[col.value] ? 'Active' : 'Inactive'}</span>
                  {col.button && (
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={item[col.value]}
                        onChange={(e) => click(e, item)}
                      />
                      <span className="slider round"></span>
                    </label>
                  )}
                </div>
              </td>
            );
          if (col.key === 'Price-component')
            return <td key={colIndex}>{currencyCode + ' ' + getFormatCurrency(item[col.value])}</td>;
          if (col.key === 'createdAt' || col.key === 'updatedAt')
            return <td key={colIndex}>{formatDate(item[col.key])}</td>;
          if (col.key === 'children') {
            return <td key={colIndex}>{col?.children(item)}</td>;
          }
          if (col.key === 'Tags' && Tags)
            return (
              <td key={colIndex}>
                <CreatableSelect
                  isClearable
                  value={item[Tags.value] ? { label: item[Tags.value], value: '' } : undefined}
                  options={Tags.options}
                  styles={Tags.styles}
                  onCreateOption={(tag) => Tags.onCreateOption(tag, index)}
                  onChange={(selectedOption) =>
                    Tags.onChange(selectedOption || { label: '', value: '' }, index)
                  }
                />
              </td>
            );
          return (
            <td key={colIndex}>
              <span
                title={stripHtml(item[col.key])}
                dangerouslySetInnerHTML={{ __html: truncateString(item[col.key], 100) }}
              />
            </td>
          );
        })}
      </tr>
    ));
  }, [data, Theads, isHover, tablebutton, onRowClick, rowClassName, click, Tags, currencyCode]);

  return (
    <>
      <div>
        <Table className={`Scroll-table Custom-Table w-100 ${className}`} responsive>
          <thead>
            <tr>
              {Theads.map((item, index) => (
                <th key={index} style={{ textAlign: 'center', width: item.width }}>
                  {item.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{memoizedTableRows}</tbody>
        </Table>
      </div>
      {footer && (
        <div className="pt-6 d-flex align-items-center justify-content-between rowssize Table-footer">
          <div className="d-flex justify-content-center align-items-center">
            <span>Rows per page</span>
            <span className="ms-4">
              <Dropdown isOpen={open} toggle={() => setOpen(!open)}>
                <DropdownToggle className="dropdown-color">
                  <div className="d-flex">
                    <span className="me-2">{itemsPerPage}</span>
                    <div>
                      <img src="/manage/images/SVG/arrow_drop_down.svg" alt="..." />
                    </div>
                  </div>
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem onClick={() => handleItemsPerPageChange(10)}>10</DropdownItem>
                  <DropdownItem onClick={() => handleItemsPerPageChange(30)}>30</DropdownItem>
                  <DropdownItem onClick={() => handleItemsPerPageChange(50)}>50</DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </span>
          </div>
          <div className="pagination">
            <div className="pagination d-flex justify-content-center align-items-center">
              <p className="d-flex align-items-center m-0">
                <span className="companytext">
                  {footerdata.currentpage} - {footerdata.totalpages} of {footerdata.totalpages}
                  {/* {/ {startIndex}-{endIndex} of {totalPages} /} */}
                </span>
              </p>
              <button className="pagination-btn" type='button' onClick={(e) => handleMoveLeft(e)}>
                {
                  footerdata.currentpage > 1 ? (
                    <img src={'/manage/images/SVG/left-active.svg'} alt="Previous page" />
                  ) : (
                    <img src={'/manage/images/SVG/left.svg'} alt="Previous page" />
                  )
                }

              </button>
              <button className="pagination-btn ms-3" type='button'  onClick={(e) => handleMoveRight(e)}>
              {
                  footerdata.currentpage === footerdata.totalpages ? (
                    <img src={'/manage/images/SVG/right_disable.svg'} alt="Previous page" />
                  ) : (
                    <img src={'/manage/images/SVG/right.svg'} alt="Previous page" />
                  )
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TableC;
